import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { gql, useQuery } from "@apollo/client";
import { IconSearch } from "@tabler/icons-react";
import { classNames, useAppConfig, useIsMounted } from "@ct-react/core";
import { useLocaleFormatter } from "@ct-react/locale";
import { SiteType } from "@shared/site-type";
import { globalTranslations } from "../../i18n/sharable-defs";
import { useDisplayMobileOnly } from "../../tools/breakpoints";
import { AmountRange, HomealpContextType, HomealpMainSearchCriteria, Resort } from "../../models/search";
import { Button } from "../common/minimals";
import Modal, { InnerModal } from "../common/modal";
import MainSearchRegionInput, { regionFormattedState } from "./search-inputs/region-input";
import MainSearchPriceRangeInput, { priceRangeFormattedState } from "./search-inputs/pricerange-input";
import MainSearchArticleTypeInput, { articleTypeFormattedState } from "./search-inputs/articletype-input";
import "./main-search-form.scss";

const RESORTS_GQL_DATA = gql`
  query ResortsList($agencyId: ID) {
    resorts: searchableAccommodationRegions(merchantId: $agencyId) { id slug name }
  }
`;

type MainSearchFormProps = {
  className?: string;
  agencyId?: string;
  criteria?: HomealpMainSearchCriteria;
  onCriteriaChange?: (criteria: HomealpMainSearchCriteria, siteType: SiteType) => void;
}

const HomealpSearchForm = (
  {
    className,
    agencyId,
    criteria = { contextType: "sale" },
    onCriteriaChange = () => void 0
  }: MainSearchFormProps) => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const { options: { siteType } } = useAppConfig();
  const isMobile = useDisplayMobileOnly();
  const { print } = useLocaleFormatter();


  // component states

  const [ contextType, setContextType ] = useState<HomealpContextType>(criteria.contextType);
  const [ region, setRegion ] = useState<Resort | undefined>(criteria.region);
  const [ priceRange, setPriceRange ] = useState<AmountRange | undefined>(criteria?.priceRange);

  const [ isOpened, setIsOpened ] = useState<boolean>(false);
  const [ openedInput, setOpenedInput ] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isMounted) return;
    setPriceRange(undefined);
  }, [ contextType ]);


  // data loading

  const { data } = useQuery<{ resorts: Resort[] }>(RESORTS_GQL_DATA, {
    ssr: false,
    variables: { agencyId }
  });

  const priceRangeProps = useMemo(() => {
    if (contextType === "rental")
      return { min: 500, max: 5000, step: 100 };
    return { min: 50000, max: 5000000, step: 50000 };
  }, [ contextType ]);


  // dom interactions

  const onInputToggle = (name: string, isOpen: boolean) => {
    setIsOpened(isOpen);
    if (isOpen && openedInput !== name) {
      setOpenedInput(name);
      return;
    }
    setOpenedInput(undefined);
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setIsOpened(false);
    onCriteriaChange({ contextType, region, priceRange }, siteType);
  }

  
  // rendering

  const wrapperClasses = classNames("rf-searcher", className, { mobile: isMobile });
  const inputs = (
    <>
      <MainSearchArticleTypeInput name="article-type"
                                  open={openedInput === "article-type"}
                                  toggle={onInputToggle}
                                  mobileView={isMobile}
                                  value={contextType}
                                  onValueChange={setContextType} />
      <MainSearchRegionInput name="regions"
                             open={openedInput === "regions"}
                             toggle={onInputToggle}
                             mobileView={isMobile}
                             resortsList={data?.resorts || []}
                             value={region}
                             onValueChange={setRegion}/>
      <MainSearchPriceRangeInput name="price"
                                 open={openedInput === "price"}
                                 toggle={onInputToggle}
                                 mobileView={isMobile}
                                 value={priceRange}
                                 onValueChange={setPriceRange}
                                 {...priceRangeProps} />
    </>);

  if (isMobile) {

    const submitter = (
      <Button type="submit" className="inline-icon">
        <IconSearch/>
        {intl.formatMessage(globalTranslations.search)}
      </Button>);

    return (
      <div className={wrapperClasses}>
        <Modal open={isOpened}
               onOpenChange={setIsOpened}>

          <InnerModal.Trigger as="div" className="input-wrap inner-space m-resume">
            <div className="r-btn link rounded-icon"><IconSearch/></div>
            <div className="resort ellipsed">{regionFormattedState(intl, region, data?.resorts || [])}</div>
            <div
              className="others ellipsed">
              {`${articleTypeFormattedState(intl, contextType)} • ${priceRangeFormattedState(intl, print.priceRange, priceRange)}`}
            </div>

            <div className="others ellipsed"></div>
          </InnerModal.Trigger>

          <InnerModal.Content footer={submitter}
                              as="form"
                              className="rf-searcher-mobile-modal-wrap"
                              onSubmit={e => onSubmit(e)}>
            {inputs}
          </InnerModal.Content>

        </Modal>
      </div>);

  }

  return (
    <form className={wrapperClasses}
          onSubmit={onSubmit}>
      {inputs}
      <div className="submitter inner-space">
        <Button type="submit" className="rounded-icon">
          <IconSearch/>
        </Button>
      </div>
    </form>);

};

export default HomealpSearchForm;
